/**
 * ToDo List - Takenlijst voor ontwikkeling
 */

import { useState, useEffect } from 'react';
import { todosService, Todo, TodoCreate, TodoPrioriteit, TodoStatus } from '../../services/todos.service';

const TodoList = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [filterStatus, setFilterStatus] = useState<TodoStatus | ''>('');

  // Form state
  const [formData, setFormData] = useState<TodoCreate>({
    titel: '',
    omschrijving: '',
    prioriteit: 'NORMAAL',
    status: 'OPEN',
    toegewezen_aan: '',
    opmerkingen: ''
  });

  useEffect(() => {
    loadTodos();
  }, [filterStatus]);

  const loadTodos = async () => {
    try {
      setLoading(true);
      const data = await todosService.getAll(filterStatus || undefined);
      setTodos(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon taken niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTodo) {
        await todosService.update(editingTodo.id, formData);
      } else {
        await todosService.create(formData);
      }
      resetForm();
      loadTodos();
    } catch (err: any) {
      setError(err.message || 'Kon taak niet opslaan');
    }
  };

  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setFormData({
      titel: todo.titel,
      omschrijving: todo.omschrijving || '',
      prioriteit: todo.prioriteit,
      status: todo.status,
      toegewezen_aan: todo.toegewezen_aan || '',
      opmerkingen: todo.opmerkingen || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Weet je zeker dat je deze taak wilt verwijderen?')) return;
    try {
      await todosService.delete(id);
      loadTodos();
    } catch (err: any) {
      setError(err.message || 'Kon taak niet verwijderen');
    }
  };

  const handleStatusChange = async (todo: Todo, newStatus: TodoStatus) => {
    try {
      await todosService.update(todo.id, { status: newStatus });
      loadTodos();
    } catch (err: any) {
      setError(err.message || 'Kon status niet bijwerken');
    }
  };

  const resetForm = () => {
    setFormData({
      titel: '',
      omschrijving: '',
      prioriteit: 'NORMAAL',
      status: 'OPEN',
      toegewezen_aan: '',
      opmerkingen: ''
    });
    setEditingTodo(null);
    setShowForm(false);
  };

  const getPrioriteitBadge = (prioriteit: TodoPrioriteit) => {
    const styles: Record<TodoPrioriteit, { bg: string; color: string }> = {
      LAAG: { bg: '#d1fae5', color: '#059669' },
      NORMAAL: { bg: '#dbeafe', color: '#1d4ed8' },
      HOOG: { bg: '#fee2e2', color: '#dc2626' }
    };
    const style = styles[prioriteit];
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        textTransform: 'uppercase'
      }}>
        {prioriteit}
      </span>
    );
  };

  const getStatusBadge = (status: TodoStatus) => {
    const styles: Record<TodoStatus, { bg: string; color: string; label: string }> = {
      OPEN: { bg: '#f3f4f6', color: '#4b5563', label: 'Open' },
      BEZIG: { bg: '#fef3c7', color: '#d97706', label: 'Bezig' },
      KLAAR: { bg: '#d1fae5', color: '#059669', label: 'Klaar' }
    };
    const style = styles[status];
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  if (loading && todos.length === 0) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>Laden...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>ToDo Lijst</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          + Nieuwe Taak
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Filter */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label style={{ fontWeight: 500 }}>Filter:</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as TodoStatus | '')}
          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
        >
          <option value="">Alle</option>
          <option value="OPEN">Open</option>
          <option value="BEZIG">Bezig</option>
          <option value="KLAAR">Klaar</option>
        </select>
        <span style={{ color: '#6b7280' }}>
          {todos.length} {todos.length === 1 ? 'taak' : 'taken'}
        </span>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>{editingTodo ? 'Taak Bewerken' : 'Nieuwe Taak'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Titel *</label>
                <input
                  type="text"
                  value={formData.titel}
                  onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Omschrijving</label>
                <textarea
                  value={formData.omschrijving}
                  onChange={(e) => setFormData({ ...formData, omschrijving: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Prioriteit</label>
                  <select
                    value={formData.prioriteit}
                    onChange={(e) => setFormData({ ...formData, prioriteit: e.target.value as TodoPrioriteit })}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value="LAAG">Laag</option>
                    <option value="NORMAAL">Normaal</option>
                    <option value="HOOG">Hoog</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TodoStatus })}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value="OPEN">Open</option>
                    <option value="BEZIG">Bezig</option>
                    <option value="KLAAR">Klaar</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Toegewezen aan</label>
                <select
                  value={formData.toegewezen_aan}
                  onChange={(e) => setFormData({ ...formData, toegewezen_aan: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="">Niemand</option>
                  <option value="Dylan">Dylan</option>
                  <option value="Wessel">Wessel</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Opmerkingen</label>
                <textarea
                  value={formData.opmerkingen}
                  onChange={(e) => setFormData({ ...formData, opmerkingen: e.target.value })}
                  rows={3}
                  placeholder="Voeg opmerkingen of notities toe..."
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                >
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTodo ? 'Opslaan' : 'Toevoegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Todo Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {todos.length === 0 ? (
          <div style={{ background: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center', color: '#6b7280' }}>
            Geen taken gevonden
          </div>
        ) : (
          todos.map(todo => (
            <div
              key={todo.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${todo.prioriteit === 'HOOG' ? '#dc2626' : todo.prioriteit === 'NORMAAL' ? '#3b82f6' : '#10b981'}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>{todo.titel}</span>
                  {todo.omschrijving && (
                    <span style={{ color: '#4b5563', marginLeft: '0.5rem' }}>- {todo.omschrijving}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem', flexShrink: 0 }}>
                  {getPrioriteitBadge(todo.prioriteit)}
                  {getStatusBadge(todo.status)}
                </div>
              </div>

              {todo.toegewezen_aan && (
                <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: '#6b7280' }}>
                  <strong>Toegewezen aan:</strong> {todo.toegewezen_aan}
                </div>
              )}

              {todo.opmerkingen && (
                <div style={{
                  background: '#f9fafb',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem'
                }}>
                  <strong>Opmerkingen:</strong><br />
                  <span style={{ whiteSpace: 'pre-wrap' }}>{todo.opmerkingen}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {todo.status !== 'KLAAR' && (
                    <button
                      onClick={() => handleStatusChange(todo, todo.status === 'OPEN' ? 'BEZIG' : 'KLAAR')}
                      className="btn btn-sm"
                      style={{ backgroundColor: '#d1fae5', color: '#059669' }}
                    >
                      {todo.status === 'OPEN' ? 'Start' : 'Klaar'}
                    </button>
                  )}
                  {todo.status === 'KLAAR' && (
                    <button
                      onClick={() => handleStatusChange(todo, 'OPEN')}
                      className="btn btn-sm"
                      style={{ backgroundColor: '#f3f4f6', color: '#4b5563' }}
                    >
                      Heropenen
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(todo)}
                    className="btn btn-sm btn-secondary"
                  >
                    Bewerken
                  </button>
                  <button
                    onClick={() => handleDelete(todo.id)}
                    className="btn btn-sm"
                    style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TodoList;
